import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  Link,
} from "@mui/material";
import axios from "axios";
import Urls from "@egovernments/digit-ui-libraries/src/services/atoms/urls";

/* ✅ Reusable Response Dialog */
const ResponseDialog = ({ open, onClose, type, response }) => {
  if (!response) return null;
  const { applicationNo, status, mobileNo, selfApprovaldrawingDetailsUrl ,drawingDetailsUrl, sanctionLetterUrl ,selfApprovalsanctionLetterUrl } =
    response?.BPA || response || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Card sx={{ borderRadius: "16px", boxShadow: 3, p: 3 }}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
          {type === "owner" ? "Owner" : "Citizen"} Response
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            <strong>Application No:</strong> {applicationNo || "N/A"}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Status:</strong> {status || "N/A"}
          </Typography>
          {type === "owner" && mobileNo && (
            <Typography variant="body1">
              <strong>Mobile:</strong> {mobileNo}
            </Typography>
          )}
          {type === "owner" && (
            <React.Fragment>
              {(selfApprovaldrawingDetailsUrl || drawingDetailsUrl) && (
                <Typography>
                  <Link href={selfApprovaldrawingDetailsUrl || drawingDetailsUrl} target="_blank" rel="noopener">
                    📄 View Drawing
                  </Link>
                </Typography>
              )}
              {(sanctionLetterUrl || selfApprovalsanctionLetterUrl) && (
                <Typography>
                  <Link href={sanctionLetterUrl || selfApprovalsanctionLetterUrl} target="_blank" rel="noopener">
                    📄 View Sanction Letter
                  </Link>
                </Typography>
              )}
            </React.Fragment>
          )}
        </DialogContent>
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button variant="contained" color="primary" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Card>
    </Dialog>
  );
};

/* ✅ "No Record Found" Popup */
const NoDataDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <Card sx={{ p: 3, textAlign: "center", borderRadius: "16px" }}>
      <Typography variant="h6" color="error" gutterBottom>
        ❌ No Record Found
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        No record found for this file number. Please check and try again.
      </Typography>
      <Button variant="contained" onClick={onClose}>
        Close
      </Button>
    </Card>
  </Dialog>
);

/* ✅ Main Dialog Component (Owner + Citizen) */
const OwnerDialog = ({ open, onClose, type = "owner" }) => {
  const [step, setStep] = useState(1);
  const [fileNumber, setFileNumber] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [alertMsg, setAlertMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [noDataDialogOpen, setNoDataDialogOpen] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const inputRefs = useRef([]);

  const tenantId = "up";
  const stateId = "up";

  /* ⏱️ Timer for OTP resend */
  useEffect(() => {
    let countdown;
    if (step === 2 && timer > 0) {
      countdown = setTimeout(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearTimeout(countdown);
  }, [timer, step]);

  /* 🔄 Reset on close */
  useEffect(() => {
    if (!open) {
      setStep(1);
      setFileNumber("");
      setMobileNumber("");
      setOtpDigits(["", "", "", "", "", ""]);
      setTimer(30);
      setCanResend(false);
      setAlertMsg("");
    }
  }, [open]);

  const validateMobile = (value) => /^[6-9]\d{9}$/.test(value);

  /* 🔹 Send OTP for Owner */
  const handleSendOtp = async () => {
    if (!validateMobile(mobileNumber)) {
      setAlertMsg("❌ Enter a valid 10-digit mobile number.");
      return;
    }
    setLoading(true);
    setAlertMsg("");

    const requestData = {
      otp: {
        mobileNumber,
        userType: "citizen",
        type: "login",
        tenantId,
      },
      RequestInfo: {
        apiId: "Rainmaker",
        msgId: `${Date.now()}|en_IN`,
      },
    };

    try {
      const res = await Digit.UserService.sendOtp(requestData, stateId);
      if (res?.isSuccessful || res?.ResponseInfo) {
        setAlertMsg(`✅ OTP sent successfully to ${mobileNumber}`);
        setStep(2);
        setTimer(30);
        setCanResend(false);
      }
    } catch (err) {
      console.error("Send OTP Error:", err);
      setAlertMsg("❌ New User? Please register your detail in the portal.");
    } finally {
      setLoading(false);
    }
  };

  /* 🔹 Verify OTP for Owner */
  const handleVerifyOtp = async () => {
    const otp = otpDigits.join("");
    if (otp.length !== 6) {
      setOtpError("Please enter 6-digit OTP");
      return;
    }
    setOtpError("");
    setLoading(true);
    setAlertMsg("");

    try {
      const res = await axios.post(`${Urls.OTP_Send_verify}`, { mobileNumber, otp });
      if (res?.data?.successful === true) {
        setAlertMsg("✅ OTP verified successfully!");
        await handleSearchApi();
      } else {
        setOtpError("❌ Invalid OTP. Please try again.");
        setTimer(30);
        setCanResend(false);
      }
    } catch (err) {
      console.error("OTP Verification Error:", err);
      setOtpError("❌ OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  /* 🔹 Common Search API (Owner + Citizen) */
  const handleSearchApi = async () => {
    try {
      const payload = {
        BPA: {
          applicationNo: fileNumber,
          mobileNumber: type === "owner" ? mobileNumber : undefined,
        },
        RequestInfo: { apiId: "Rainmaker", msgId: `${Date.now()}|en_IN` },
      };

      const res = await axios.post(
        `${Urls.trackAppliactionStatus}`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
        console.log("Search API Response:", res.data);
        
      if (!res?.data?.message) {
        setResponseData(res.data);
        setResponseDialogOpen(true);
      } else if(res?.data?.message) {
        setNoDataDialogOpen(true);
      }
    } catch (error) {
      console.error("Search API Error:", error);
      setNoDataDialogOpen(true);
    } finally {
      onClose(); // always close OTP / main dialog
    }
  };

  /* 🔹 Citizen Direct Search */
  const handleCitizenSearch = async () => {
    if (!fileNumber) return setAlertMsg("❌ Please enter file number.");
    await handleSearchApi();
  };

  /* OTP Input Control */
  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otpDigits];
    newOtp[index] = value;
    setOtpDigits(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <React.Fragment>
      {/* 🔸 Main Dialog */}
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <Card sx={{ borderRadius: "16px", boxShadow: 3, p: 2 }}>
          <DialogTitle
            sx={{
              background: "#E6E6F3",
              color: "#003366",
              fontWeight: "bold",
              py: 1.5,
              mb: 2,
            }}
          >
            {type === "owner"
              ? step === 1
                ? "Mobile Verification"
                : "OTP Verification"
              : "Citizen Search"}
          </DialogTitle>

          <DialogContent>
            {alertMsg && (
              <Alert
                severity={alertMsg.includes("✅") ? "success" : "info"}
                sx={{ mb: 2 }}
              >
                {alertMsg}
              </Alert>
            )}

            {/* Step 1 - File + Mobile for Owner */}
            {type === "owner" && step === 1 && (
              <Box>
                <TextField
                  label="File Number"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={fileNumber}
                  onChange={(e) => setFileNumber(e.target.value)}
                />
                <TextField
                  label="Mobile Number"
                  fullWidth
                  value={mobileNumber}
                  onChange={(e) =>
                    setMobileNumber(e.target.value.replace(/\D/g, ""))
                  }
                  inputProps={{ maxLength: 10 }}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleSendOtp}
                  disabled={loading || !fileNumber || mobileNumber.length < 10}
                >
                  {loading ? "Sending OTP..." : "Get OTP"}
                </Button>
              </Box>
            )}

            {/* Step 2 - OTP for Owner */}
            {type === "owner" && step === 2 && (
              <Box textAlign="center">
                <Typography sx={{ mb: 1 }}>
                  Enter the OTP sent to <strong>{mobileNumber}</strong>
                </Typography>

                <Grid container justifyContent="center" spacing={1} sx={{ mb: 2 }}>
                  {otpDigits.map((digit, idx) => (
                    <Grid item key={idx}>
                      <TextField
                        inputRef={(el) => (inputRefs.current[idx] = el)}
                        value={digit}
                        onChange={(e) => handleOtpChange(e.target.value, idx)}
                        onKeyDown={(e) => handleKeyDown(e, idx)}
                        inputProps={{
                          maxLength: 1,
                          style: { textAlign: "center", width: "40px" },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>

                {otpError && (
                  <Typography color="error" sx={{ mb: 2 }}>
                    {otpError}
                  </Typography>
                )}

                {!canResend ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Resend OTP in {timer} seconds
                  </Typography>
                ) : (
                  <Button
                    variant="text"
                    color="primary"
                    onClick={handleSendOtp}
                    sx={{ mb: 2 }}
                  >
                    Resend OTP
                  </Button>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleVerifyOtp}
                  disabled={loading}
                >
                  {loading ? "Verifying..." : "Next"}
                </Button>
              </Box>
            )}

            {/* 🔹 Citizen Search */}
            {type === "citizen" && (
              <Box>
                <TextField
                  label="File Number"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={fileNumber}
                  onChange={(e) => setFileNumber(e.target.value)}
                />
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleCitizenSearch}
                  disabled={loading || !fileNumber}
                >
                  {loading ? "Searching..." : "Search"}
                </Button>
              </Box>
            )}
          </DialogContent>
        </Card>
      </Dialog>

      {/* 🔸 Response Dialog */}
      <ResponseDialog
        open={responseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
        type={type}
        response={responseData}
      />

      {/* 🔸 No Data Dialog */}
      <NoDataDialog
        open={noDataDialogOpen}
        onClose={() => setNoDataDialogOpen(false)}
      />
    </React.Fragment>
  );
};

export default OwnerDialog;
